import axiosInstance from "./axiosInstance";
import { ApiResponse } from "./pointsService";
import { log, LogLevel } from "utils/LogUtil";

/**
 * PhAI 약국 가입 관리(admin) 서비스 — 계약 §4.4 / admin-frontend spec §4.2
 *
 * 가입 약국 목록 조회 + 정지/해제(운영 제재) + 사업자등록증 사후검증(D14, 계약 §4.6).
 * D1 자동승인이라 대기열(승인 화면)은 없다 — 관측과 제재만 담당한다.
 *
 * ⚠️ 백엔드 admin API(P1-d)는 아직 구현 전이다. 미구현(404)·서버오류 시에도 화면이 죽지
 * 않도록, 이 레이어에서 예외를 흡수해 `ApiResponse` 형태로 되돌린다(adminSubscriptionService
 * 관례). HTTP 오류는 `error`에 상태코드를 실어 호출부가 404(미구현)를 구분할 수 있게 한다.
 */

const BASE = "/api/v1/products/pharmacy/admin/access";

/** 접근 상태 — 계약 §4.1: P1 방출값은 NONE/APPROVED/SUSPENDED 3종(APPLIED·REJECTED는 예약). */
export type PharmacyAccessStatus = "NONE" | "APPROVED" | "SUSPENDED" | string;

/** 가입 약국 1건 — `users_pharmacy` JOIN `users`(계약 §4.4). */
export interface PharmacyAccessRow {
  userId: string;
  pharmacyName: string | null;
  bizRegNo: string | null; // 사업자등록번호
  postalCode: string | null; // 우편번호
  address: string | null;
  addressDetail: string | null;
  joinedAt: string | null; // 계약: 약관 동의 시각(= users_pharmacy.joined_at)
  agreementVersion: string | null; // 약관 세트 버전
  status: PharmacyAccessStatus;
  suspendedAt: string | null;
  suspendReason: string | null;
  certVerifiedAt: string | null; // null = 미검증(계약 §4.6, "아직 안 봤다"이지 "문제 있다"가 아니다)
  user: { email: string | null; name: string | null };
}

export interface PharmacyAccessListResult {
  items: PharmacyAccessRow[];
  total?: number;
}

export interface PharmacyAccessListParams {
  status?: string;
  limit: number;
  offset: number;
}

/** 사업자등록증 서명 URL 발급 응답(단기 TTL). URL은 목록에 미리 싣지 않고 그때 발급한다(계약 §4.6). */
export interface CertUrlResult {
  url: string;
}

const pick = <T>(...vals: T[]): T | null => {
  for (const v of vals) if (v !== undefined && v !== null) return v;
  return null;
};

/**
 * 백엔드 DTO 확정 전이라 방어적으로 매핑한다. 계약이 컬럼을 snake_case로 참조(`joined_at`·
 * `care_org_no`·`biz_reg_no`·`postal_code`·`cert_verified_at`)하는 한편 `me` 응답은 camelCase라,
 * 실제 admin DTO가 어느 표기로 오든 화면이 성립하도록 두 표기를 모두 읽는다.
 */
const normalizeRow = (raw: any): PharmacyAccessRow => {
  const u = raw?.user ?? {};
  return {
    userId: String(pick(raw?.userId, raw?.user_id, u?.id, raw?.id) ?? ""),
    pharmacyName: pick(raw?.pharmacyName, raw?.pharmacy_name),
    bizRegNo: pick(raw?.bizRegNo, raw?.biz_reg_no),
    postalCode: pick(raw?.postalCode, raw?.postal_code),
    address: pick(raw?.address),
    addressDetail: pick(raw?.addressDetail, raw?.address_detail),
    joinedAt: pick(raw?.joinedAt, raw?.joined_at, raw?.appliedAt, raw?.applied_at),
    agreementVersion: pick(raw?.agreementVersion, raw?.agreement_version),
    status: (pick(raw?.status) as PharmacyAccessStatus) ?? "NONE",
    suspendedAt: pick(raw?.suspendedAt, raw?.suspended_at),
    suspendReason: pick(raw?.suspendReason, raw?.suspend_reason),
    certVerifiedAt: pick(raw?.certVerifiedAt, raw?.cert_verified_at),
    user: {
      email: pick(u?.email, raw?.email),
      name: pick(u?.name, raw?.name),
    },
  };
};

/** HTTP 예외를 ApiResponse로 흡수 — 404(미구현)는 error=404로 실어 호출부가 구분하게 한다. */
const toErrorResponse = (error: any, fallback: string): ApiResponse<never> => {
  const status = error?.response?.status;
  // 서버가 { error, message } 본문을 준 경우 그대로 전달(상태코드 우선 승격).
  if (error?.response?.data && typeof error.response.data === "object") {
    const body = error.response.data;
    return { error: status ?? body.error ?? 1, message: body.message ?? fallback };
  }
  return { error: status ?? 1, message: fallback };
};

/** 가입 약국 목록. status 미설정 시 미전송(전체). limit/offset 페이지네이션(계약과 동일). */
export const listPharmacyAccess = async (
  params: PharmacyAccessListParams
): Promise<ApiResponse<PharmacyAccessListResult>> => {
  try {
    const query: Record<string, string | number> = {
      limit: params.limit,
      offset: params.offset,
    };
    if (params.status) query.status = params.status;

    const response = await axiosInstance.get(`${BASE}`, { params: query });
    const body = response?.data;

    // { error, data: { items, total } } 또는 { error, data: [...] } / 배열 직반환 모두 흡수.
    const payload = body && typeof body === "object" && "data" in body ? body.data : body;
    const rawItems: any[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
      ? payload.items
      : [];
    const total: number | undefined =
      typeof payload?.total === "number" ? payload.total : body?.meta?.totalItems;

    return { error: 0, data: { items: rawItems.map(normalizeRow), total } };
  } catch (error) {
    log(LogLevel.ERROR, "약국 가입 목록 조회 실패:", error);
    return toErrorResponse(error, "약국 가입 목록을 불러오지 못했습니다.");
  }
};

/** 정지 — suspended_at·suspend_reason 세팅. 사유 필수(호출부에서 검증). */
export const suspendPharmacyAccess = async (
  userId: string,
  reason: string
): Promise<ApiResponse<PharmacyAccessRow>> => {
  try {
    const response = await axiosInstance.post(`${BASE}/${userId}/suspend`, { reason });
    return { error: 0, data: response?.data?.data ?? response?.data };
  } catch (error) {
    log(LogLevel.ERROR, "약국 접근 정지 실패:", error);
    return toErrorResponse(error, "정지 처리에 실패했습니다.");
  }
};

/** 정지 해제. */
export const unsuspendPharmacyAccess = async (
  userId: string
): Promise<ApiResponse<PharmacyAccessRow>> => {
  try {
    const response = await axiosInstance.post(`${BASE}/${userId}/unsuspend`);
    return { error: 0, data: response?.data?.data ?? response?.data };
  } catch (error) {
    log(LogLevel.ERROR, "약국 접근 정지 해제 실패:", error);
    return toErrorResponse(error, "정지 해제에 실패했습니다.");
  }
};

/**
 * 사업자등록증 서명 URL 발급(단기 TTL). 클릭 시에만 호출한다 — 목록 응답에 URL을 미리 싣지
 * 않는다(개인정보 노출면 축소, 계약 §4.6).
 */
export const getPharmacyCertUrl = async (
  userId: string
): Promise<ApiResponse<CertUrlResult>> => {
  try {
    const response = await axiosInstance.get(`${BASE}/${userId}/cert-url`);
    const payload =
      response?.data && typeof response.data === "object" && "data" in response.data
        ? response.data.data
        : response?.data;
    const url: unknown =
      typeof payload === "string" ? payload : payload?.url ?? payload?.signedUrl;
    if (typeof url !== "string" || url.length === 0) {
      return { error: 1, message: "서명 URL 응답 형식이 올바르지 않습니다." };
    }
    return { error: 0, data: { url } };
  } catch (error) {
    log(LogLevel.ERROR, "사업자등록증 URL 발급 실패:", error);
    return toErrorResponse(error, "사업자등록증을 여는 데 실패했습니다.");
  }
};

/**
 * 서류 검증 완료 표식 — cert_verified_at·cert_verified_by 기록. 표식일 뿐 기능에 영향 없음
 * (잠기지 않음, 계약 §4.6). 불일치 시엔 별도 반려 상태 없이 기존 정지 경로를 쓴다.
 */
export const verifyPharmacyCert = async (
  userId: string
): Promise<ApiResponse<PharmacyAccessRow>> => {
  try {
    const response = await axiosInstance.post(`${BASE}/${userId}/cert-verify`);
    return { error: 0, data: response?.data?.data ?? response?.data };
  } catch (error) {
    log(LogLevel.ERROR, "사업자등록증 검증 완료 처리 실패:", error);
    return toErrorResponse(error, "검증 완료 처리에 실패했습니다.");
  }
};
